import { defineComponent as _defineComponent } from 'vue'
import {
  toDataModel,
  updateModel,
  setState,
  getChildModel,
  getState,
  makeFormModel,
} from 'ah-common-lib/src/form/helpers';
import { FieldOptionObj, FormDefinition, FormEvent } from 'ah-common-lib/src/form/interfaces';
import { FormValidation } from 'ah-common-lib/src/form/interfaces';
import { useSettingsStore } from '@/app/store/settingsModule';
import { countryHasStates, countryNameFromCC } from 'ah-common-lib/src/helpers/countries';
import { PropType, computed, reactive, watch } from 'vue';
import { Address } from 'ah-api-gateways';
import { isEqual } from 'lodash';
import { maxLength } from '@vuelidate/validators';
import { textField, selectField } from 'ah-common-lib/src/form/models';
import { requiredIfStateValue } from 'ah-common-lib/src/form/validators';


export default /*#__PURE__*/_defineComponent({
  __name: 'RegistrationAddressForm',
  props: {
  address: {
    type: Object as PropType<Partial<Address>>,
    required: false,
  },
  isCountryEditable: {
    type: Boolean,
    required: true,
  },
  useThreeColumns: {
    type: [Boolean, String],
    default: false,
  },
},
  emits: ["update:address", "update:validation"],
  setup(__props, { expose, emit }: { emit: ({
  (e: 'update:address', value: Partial<Address>): void;
  (e: 'update:validation', value: FormValidation<Partial<Address>>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;





const addressForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'address',
    fieldType: 'form',
    fields: [
      textField(
        'addressLine',
        'Address Line 1',
        {
          fieldWrapperClass: props.useThreeColumns !== false ? 'col col-4 px-4' : 'col col-12',
          required: true,
          showRequiredMarkers: true,
          errorMessages: { maxLength: 'Address Line1 cannot have more than 150 characters.' },
        },
        { maxLength: maxLength(150), required: requiredIfStateValue('addressLine') }
      ),
      textField(
        'city',
        'City',
        {
          fieldWrapperClass: props.useThreeColumns !== false ? 'col col-4 px-4' : 'col col-6',
          required: true,
          showRequiredMarkers: true,
          errorMessages: { maxLength: 'City cannot have more than 50 characters.' },
        },
        { maxLength: maxLength(50), required: requiredIfStateValue('city') }
      ),
      textField(
        'stateOrProvince',
        'State/Province/County',
        {
          fieldWrapperClass: props.useThreeColumns !== false ? 'col col-4 px-4' : 'col col-6',
          required: true,
          showRequiredMarkers: true,
          errorMessages: { maxLength: 'State/Province cannot have more than 50 characters.' },
        },
        { maxLength: maxLength(50), required: requiredIfStateValue('stateOrProvince') }
      ),
      textField(
        'postalCode',
        'Postcode / ZIP code',
        {
          fieldWrapperClass: props.useThreeColumns !== false ? 'col col-4 px-4' : 'col col-6',
          required: true,
          errorMessages: { maxLength: 'Postcode cannot have more than 10 digits.' },
          showRequiredMarkers: true,
        },
        { maxLength: maxLength(10), required: requiredIfStateValue('postalCode') }
      ),
      selectField(
        'countryCode',
        'Country',
        [],
        {
          fieldWrapperClass: props.useThreeColumns !== false ? 'col col-4 px-4' : 'col col-6',
          placeholder: 'Select One',
          required: true,
          showRequiredMarkers: true,
          readonly: props.isCountryEditable === false,
        },
        { required: requiredIfStateValue('countryCode') }
      ),
    ],
  }),
  validation: null,
});

const settingsStore = useSettingsStore();

const validation = computed(() => {
  return {
    ...addressForm.validation,
    $model: addressForm.form,
    $invalid: !!addressForm.validation?.$invalid,
    $dirty: !!addressForm.validation?.$dirty,
  } as FormValidation<any>;
});

function getModel(fieldName: string) {
  return getChildModel(addressForm.form, fieldName);
}

watch(
  () => props.address,
  () => updateModel(addressForm.form, props.address ?? {}),

  { immediate: true }
);

watch(
  () => props.address?.countryCode,
  () => {
    const stateOrProvinceCode = getChildModel(addressForm.form, 'stateOrProvince')!;
    const currentState = getState(stateOrProvinceCode, 'fieldType');
    const countryCode = props.address?.countryCode || '';

    const countryName = countryNameFromCC(countryCode);
    if (countryName && addressForm.form.countryName !== countryName) {
      addressForm.form.countryName = countryName;
    }

    if (countryHasStates(countryCode)) {
      setState(stateOrProvinceCode, 'fieldType', 'select');
      settingsStore.loadISOCodes({ countryCode }).then((states) => {
        // Try to keep the value of State, searching by state code OR state name, among the available options
        if (addressForm.form.stateOrProvince) {
          const state = states.find(
            (item) =>
              item.subdivisionName === addressForm.form.stateOrProvince ||
              item.code === addressForm.form.stateOrProvince
          );
          addressForm.form.stateOrProvince = state ? state.code : null;
        }
        setState(
          stateOrProvinceCode,
          'options',
          states.map((s) => ({
            value: s.code,
            label: s.subdivisionName,
          }))
        );
      });
    } else if (currentState !== 'text') {
      // Try to keep the value of selected State, if any, converting to label for freetext
      const options = getState(stateOrProvinceCode, 'options', []) as FieldOptionObj[];
      const selected = options.find((option) => option.value === addressForm.form.stateOrProvince);
      addressForm.form.stateOrProvince = selected ? selected.label : null;
      setState(stateOrProvinceCode, 'fieldType', 'text');
    }
  },
  { immediate: true }
);

function onFormEvent(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    emit('update:address', { ...props.address, ...toDataModel(addressForm.form) });
  }
}

watch(
  validation,
  (newVal, oldVal) => {
    if (!isEqual(newVal, oldVal)) {
      emit('update:validation', validation.value);
    }
  },
  { immediate: true }
);

expose({ getModel });

const countryCodeField = getChildModel(addressForm.form, 'countryCode')!;
settingsStore.loadCountries(true).then(() => {
  const countries = settingsStore.allCountries.map((country) => ({
    value: country.cc,
    label: country.name,
  }));

  setState(countryCodeField, 'options', countries);
});

return { __sfc: true,props, emit, addressForm, settingsStore, validation, getModel, onFormEvent, countryCodeField }
}

})